import { IosDependenciesReleaseReport, IosDependenciesReleaseResponse } from '@/types/iosDependencies';
import {
    FETCH_IOS_DEPENDENCIES_FOR_RELEASE_SUCCESS,
    IosDependenciesAction,
    UPDATE_IOS_DEPENDENCIES_FOR_RELEASE,
    UPDATE_IOS_DEPENDENCIES_REPORT_FOR_RELEASE,
} from '@/actions/iosDependencies';
import type { RootState } from '@/reducers/reducers';

export interface IosDependenciesState {
    byReleaseId: Record<string, IosDependenciesReleaseResponse>;
}

const initialState: IosDependenciesState = {
    byReleaseId: {},
};

const mergeReportIntoResponse = (
    existing: IosDependenciesReleaseResponse | undefined,
    incoming: IosDependenciesReleaseReport,
): IosDependenciesReleaseResponse => {
    const baseReports = existing?.reports ?? [];
    const reportIdx = baseReports.findIndex((report) => report.destinationId === incoming.destinationId);
    let mergedReports: IosDependenciesReleaseReport[];
    if (reportIdx === -1) {
        mergedReports = [...baseReports, incoming];
    } else {
        mergedReports = [...baseReports];
        mergedReports[reportIdx] = incoming;
    }
    return {
        hasConfigs: mergedReports.length > 0 ? true : (existing?.hasConfigs ?? false),
        reports: mergedReports,
    };
};

export const selectIosDependenciesForRelease = (
    state: RootState,
    releaseId: string,
): IosDependenciesReleaseResponse | undefined => state.iosDependencies.byReleaseId[releaseId];

export default function iosDependenciesReducer(
    state: IosDependenciesState = initialState,
    action: IosDependenciesAction,
): IosDependenciesState {
    switch (action.type) {
        case FETCH_IOS_DEPENDENCIES_FOR_RELEASE_SUCCESS:
        case UPDATE_IOS_DEPENDENCIES_FOR_RELEASE: {
            const { releaseId, response } = action.payload;
            return {
                ...state,
                byReleaseId: { ...state.byReleaseId, [releaseId]: response },
            };
        }
        case UPDATE_IOS_DEPENDENCIES_REPORT_FOR_RELEASE: {
            const { releaseId, report } = action.payload;
            const merged = mergeReportIntoResponse(state.byReleaseId[releaseId], report);
            return {
                ...state,
                byReleaseId: { ...state.byReleaseId, [releaseId]: merged },
            };
        }
        default:
            return state;
    }
}
